// Recorridos guiados de Tienda online — ADR-2026-10-01-recorridos-de-tienda-online.
//
// Los MISMOS cartelitos y el mismo velo oscuro del Estudio de Video
// (`startContextualTour` de `lib/editorTour`, driver.js con el tema de Atlas): una
// sola pieza para "te explico esta pantalla" en toda la app.
//
// Son cuatro recorridos cortos, cada uno atado a lo que la persona tiene delante —la
// lista vacía, un lanzamiento vacío, uno con productos, la ficha— en vez de un tour
// largo al entrar: cada cartel aparece cuando la cosa de la que habla está en
// pantalla. Cada uno se ve una vez por cuenta (`profiles.onboarding_seen`).
import { useEffect } from 'react';
import type { DriveStep } from 'driver.js';
import { startContextualTour } from '../../lib/editorTour';
import { isSeen, loadOnboardingSeen, markSeen, type OnboardingKey } from '../../lib/onboardingSeen';
import { supabase } from '../../lib/supabase';
import { anuncioPendiente } from '../../lib/anuncios';

/**
 * Hay un recorrido andando. Es de módulo y no de React porque los tres viven en
 * componentes distintos: abrir la ficha en medio del recorrido de la tabla (el paso de
 * "Modificar" lo invita) no puede arrancar el de la ficha encima del otro.
 */
let enCurso = false;
/** Cómo cortar el que está andando, para `cerrarRecorridoEnCurso`. */
let cortarEnCurso: (() => void) | null = null;

/**
 * Corta el recorrido que esté en pantalla. Para cuando la persona HACE lo que el
 * cartel le señala (toca "Nuevo lanzamiento"): el cartel ya cumplió, y dejarlo encima
 * del modal que acaba de abrir lo taparía. Corre su `onDone`, así que queda visto.
 */
export function cerrarRecorridoEnCurso(): void {
  cortarEnCurso?.();
}

/**
 * ¿Hay algo encima que un cartel taparía o que lo taparía a él? `dentroDeUnModal` es
 * para el recorrido que explica un modal: ese modal no cuenta como "algo encima".
 */
function hayAlgoEncima(dentroDeUnModal: boolean): boolean {
  return (
    enCurso ||
    // El popup de funcionalidad nueva (`AnuncioModal`): primero se lee eso. No alcanza
    // con buscarlo en el DOM — tarda en decidir y el recorrido llegaba antes —, así que
    // se pregunta si está mostrándose O todavía decidiendo.
    anuncioPendiente() ||
    !!document.querySelector('[aria-labelledby="anuncio-titulo"]') ||
    // Cualquier otro modal de la pantalla (crear, conectar la tienda, confirmar).
    (!dentroDeUnModal && !!document.querySelector('[aria-modal="true"]'))
  );
}

/** El paso sólo entra si su elemento está montado Y se ve (no `hidden` por breakpoint). */
function visible(paso: DriveStep): boolean {
  if (typeof paso.element !== 'string') return true;
  const el = document.querySelector(paso.element);
  return !!el && el.getClientRects().length > 0;
}

/**
 * Lanza el recorrido `clave` cuando `listo` sea true, una sola vez por cuenta.
 *
 * `pasos` se arma en el momento de arrancar y se filtra por lo que se ve: según el
 * destino, el ancho de pantalla o si el producto ya existe en la tienda, hay botones
 * que no están, y un cartel apuntando a la nada es peor que uno menos. Si no queda
 * ninguno, no arranca y NO se marca visto — no se vio nada.
 *
 * Espera a que no haya nada encima (otro recorrido, un anuncio, un modal) en vez de
 * rendirse: el anuncio de la sección sale justo en la primera visita, que es también
 * cuando corresponde este recorrido.
 */
export function useRecorrido(
  clave: OnboardingKey,
  listo: boolean,
  pasos: () => DriveStep[],
  opciones: { dentroDeUnModal?: boolean } = {},
) {
  const dentroDeUnModal = opciones.dentroDeUnModal ?? false;
  useEffect(() => {
    if (!listo) return;
    let vivo = true;
    let espera: ReturnType<typeof setTimeout> | null = null;
    let cortar: (() => void) | null = null;

    void (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data?.user?.id ?? null;
      if (!uid || !vivo) return;
      await loadOnboardingSeen(uid);
      if (!vivo || isSeen(clave)) return;

      const intentar = (restantes: number) => {
        if (!vivo) return;
        if (hayAlgoEncima(dentroDeUnModal)) {
          // Medio segundo entre miradas, hasta cinco minutos: el anuncio de la sección
          // se lee con calma, y el cartel tiene que estar ahí cuando lo cierre.
          if (restantes > 0) espera = setTimeout(() => intentar(restantes - 1), 500);
          return;
        }
        const filtrados = pasos().filter(visible);
        if (filtrados.length === 0) return;
        enCurso = true;
        cortar = startContextualTour({
          steps: filtrados,
          onDone: () => {
            enCurso = false;
            cortarEnCurso = null;
            markSeen(clave, uid);
          },
        });
        if (!cortar) enCurso = false;
        else cortarEnCurso = cortar;
      };
      // Un respiro para que la pantalla termine de pintarse: el velo mide los elementos.
      espera = setTimeout(() => intentar(600), 600);
    })();

    return () => {
      vivo = false;
      if (espera) clearTimeout(espera);
      // Se fue de la pantalla en medio: se corta. `onDone` corre igual y lo da por
      // visto — volver a mostrarlo entero por haber cambiado de pantalla sería peor.
      cortar?.();
    };
    // `pasos` se lee al arrancar, a propósito: no tiene que reiniciar el recorrido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, listo, dentroDeUnModal]);
}

/** El primero del módulo: la lista vacía, apuntando a "Nuevo lanzamiento". */
export function pasosDelInicio(): DriveStep[] {
  return [
    {
      element: '[data-tour="tienda-nuevo"]',
      popover: {
        title: 'Empezá por acá',
        description:
          'Un lanzamiento junta varios productos con sus fotos para publicarlos todos juntos. Ponele un nombre, elegí a dónde va —tu Tienda Nube o un ZIP— y después sumale tus creaciones.',
        side: 'bottom',
        align: 'end',
      },
    },
    // Las métricas sin tiendas conectadas, debajo de los lanzamientos
    // (ADR-2026-10-06-ensayo-de-primera-vez). Si el bloque no está en pantalla —tiendas ya
    // conectadas, o métricas apagadas— `visible` saca el paso y queda sólo el de arriba.
    {
      element: '[data-tour="tienda-conectar"]',
      popover: {
        title: 'Y acá, tus ventas',
        description:
          'Conectá Tienda Nube o Mercado Libre y acá vas a ver cuánto vendés: lo de las dos sumado y lo de cada una por separado.',
        side: 'top',
        align: 'start',
      },
    },
  ];
}

/** Adentro del modal de crear: lo que el modal no dice del destino. */
export function pasosDelDestino(): DriveStep[] {
  return [
    {
      element: '[data-tour="tienda-destino"]',
      popover: {
        title: 'Elegí bien el destino',
        description:
          'Decide qué datos te pide cada producto y qué hace el botón de publicar: Tienda Nube crea o actualiza los productos en tu tienda y Otra plataforma te baja un ZIP. Después no se cambia: si te equivocás, creá otro.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/** El recorrido de un lanzamiento con productos. */
export function pasosDelLanzamiento(descarga: boolean): DriveStep[] {
  return [
    // El panel de arriba (ADR-2026-10-05-la-tabla-de-la-tienda-dice-que-falta).
    {
      element: '[data-tour="tienda-panel"]',
      popover: {
        title: descarga ? 'Cómo sale y en qué paso vas' : 'Tu tienda y en qué paso vas',
        description: descarga
          ? 'Lo tildado se descarga en un ZIP. Abajo ves los tres pasos del lanzamiento y cuánto falta de cada uno.'
          : 'Acá ves a qué tienda va el lanzamiento y si está conectada. Abajo, los tres pasos y cuánto falta de cada uno.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tienda-tabla"]',
      popover: {
        title: 'Tus productos',
        description:
          'Cada fila es un producto con sus fotos, y la columna Estado te dice si ya está listo o qué le falta. Lo tildado es lo que se publica: destildá lo que todavía no quieras subir.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tienda-lote"]',
      popover: {
        title: 'Todo junto',
        description:
          'Lo que hagas acá vale para todos los tildados: completar las fichas con IA, ponerles una categoría o sacarlos del lanzamiento.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tienda-modificar"]',
      popover: {
        title: 'La ficha de cada producto',
        description:
          'Abrila para completar nombre, precio y stock, y para ordenar las fotos. La primera es la portada.',
        side: 'left',
        align: 'center',
      },
    },
    {
      element: '[data-tour="tienda-categorias"]',
      popover: {
        title: 'Las categorías de tu tienda',
        description:
          'Armá el árbol de categorías una vez y usalo en todos los productos. Las que no existan en tu tienda se crean al publicar.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: '[data-tour="tienda-publicar"]',
      popover: {
        title: descarga ? 'Descargá todo' : 'Publicá en un paso',
        description: descarga
          ? 'Bajás un ZIP con las fotos de lo tildado, ordenadas en carpetas por categoría.'
          : 'Sube lo tildado a tu tienda: crea los productos nuevos y suma las fotos a los que ya están. Si a alguno le falta un dato, te decimos cuál.',
        side: 'bottom',
        align: 'end',
      },
    },
  ];
}

/** El recorrido de un lanzamiento recién creado, sin productos. */
export function pasosDelLanzamientoVacio(): DriveStep[] {
  return [
    {
      element: '[data-tour="tienda-elegir"]',
      popover: {
        title: 'Empezá por tus creaciones',
        description:
          'Elegí las fotos que ya generaste. Cada una entra con su producto, y si no tiene, lo elegís en el momento.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/** El recorrido de la ficha de un producto. */
export function pasosDeLaFicha(): DriveStep[] {
  return [
    {
      element: '[data-tour="ficha-estado"]',
      popover: {
        title: '¿Está listo?',
        description:
          'Acá ves si el producto se puede publicar o qué dato le falta. Los cambios se guardan solos.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="ficha-ia"]',
      popover: {
        title: 'Completar con IA',
        description:
          'Te propone descripción, etiquetas, SEO y categorías mirando las fotos. Vos elegís qué aplicar.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="ficha-accion"]',
      popover: {
        title: 'Este producto ya está en tu tienda',
        description:
          'Elegí si las fotos se suman a las que ya tiene o las reemplazan. Su ficha no se toca.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="ficha-fotos"]',
      popover: {
        title: 'Las fotos',
        description:
          'Arrastralas para ordenarlas: la primera es la portada. Con “Agregar” sumás otra creación o subís una foto.',
        side: 'top',
        align: 'start',
      },
    },
  ];
}
